import { Badge, Box, Flex, Heading, HStack, Spinner, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { type PortalSourcingEventDetailDto } from "../../../../../autogen/bff-api";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { MinimumRequirementAnswerCard } from "../MinimumRequirementAnswerCard";
import { useMinimumRequirementAnswers } from "../useMinimumRequirementAnswers";

interface Props {
  bidId: string;
  event: PortalSourcingEventDetailDto;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

// t('Minimum requirements') t('Buyer criteria')
// t('Answer the buyer's minimum requirements.')

export const MinimumRequirementsStep = ({ bidId, event, previousStep, nextStep, nextStepLabel }: Props) => {
  const { t } = useTranslation();
  const answers = useMinimumRequirementAnswers(bidId);

  const requirements = event.minimumRequirements ?? [];
  const answeredCount = answers.answeredCount(requirements);

  return (
    <Box pb="8" pt="6">
      <Flex align="center" justify="space-between" gap="3" mb="2" flexWrap="wrap">
        <HStack spacing="2" align="center" flexWrap="wrap">
          <Heading size="md">{t("Minimum requirements")}</Heading>
          <Badge bg="smPrimarySofter" color="smPrimary" px="2" py="0.5" borderRadius="md" textTransform="none">
            {t("Buyer criteria")}
          </Badge>
          {answers.isSaving && <Spinner size="sm" color="smMuted" />}
        </HStack>
        <Text
          fontSize="sm"
          fontWeight="semibold"
          color={requirements.length > 0 && answeredCount === requirements.length ? "smSuccess" : "smMuted"}
        >
          {t("{{answered}} of {{total}} answered", { answered: answeredCount, total: requirements.length })}
        </Text>
      </Flex>
      <Text fontSize="sm" color="smMuted" mb="6">
        {t("Answer the buyer's minimum requirements.")}
      </Text>

      <Stack spacing="3" mb="6">
        {requirements.map((requirement) => (
          <MinimumRequirementAnswerCard
            key={requirement.id ?? ""}
            requirement={requirement}
            response={answers.responseFor(requirement)}
            isReadOnly={answers.readOnly}
            onAnswerChange={(answer) =>
              answers.upsert(requirement, answer ? { answer: true, comment: null } : { answer: false })
            }
            onCommentChange={(comment) => answers.upsert(requirement, { comment })}
          />
        ))}
      </Stack>

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />
    </Box>
  );
};
